import posthog from 'posthog-js'

const posthogDebugEnabled = process.env.NODE_ENV === 'development'

/**
 * Share of funnel sessions to record (Ben, 2026-07-30: 5%).
 *
 * Sampled HERE, in this app, rather than via PostHog's project-level
 * `session_recording_sample_rate` — that setting is shared with
 * `web.roast.dating`, which records at 100% in the same project (127405).
 * Turning the project rate down to 5% for the funnel's sake would have cut the
 * main app's replay coverage 20x.
 *
 * posthog-js only honours `sampleRate` from REMOTE config, so the client-side
 * equivalent is to start recording off and opt the sampled share in via
 * `startSessionRecording()` (which flips `disable_session_recording` itself).
 */
const REPLAY_SAMPLE_RATE = Number(process.env.NEXT_PUBLIC_POSTHOG_REPLAY_SAMPLE_RATE ?? '0.05')

/**
 * Stable per-session sampling decision.
 *
 * Keyed on the session id, NOT `Math.random()`: the funnel spans many page loads
 * (`/` → `/latest/new` → upload → photos → paywall) and a per-load coin flip would
 * record a disconnected handful of screens from each visitor instead of whole
 * journeys — useless for the drop-off questions replay was turned on to answer.
 *
 * FNV-1a, so the bucket is deterministic and evenly spread over session ids.
 */
function isSampledForReplay(sessionId: string, rate: number): boolean {
  if (!Number.isFinite(rate) || rate <= 0) return false
  if (rate >= 1) return true

  let hash = 0x811c9dc5
  for (let i = 0; i < sessionId.length; i++) {
    hash ^= sessionId.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }

  return (hash >>> 0) % 10_000 < rate * 10_000
}

posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
  api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
  ui_host: 'https://eu.posthog.com',
  defaults: '2026-01-30',
  capture_exceptions: true,
  // Replay is ON for a sampled share as of 2026-07-30. Until then this was an
  // unconditional `disable_session_recording: true`, and the funnel was a black
  // box: every "why did they drop at upload?" question could only be inferred
  // from event counts.
  //
  // Still `true` at init — `loaded` below starts recording for the sampled
  // share only. Do not "simplify" this to `false`: that records 100% of funnel
  // traffic.
  disable_session_recording: true,
  session_recording: {
    // Every input in this funnel is personal — email, the quiz free-text, the
    // Instagram/Tinder handles. The project's remote masking default is
    // `maskAllInputs: false`, so this client value is what actually protects them.
    maskAllInputs: true,
    // The visitor's own uploaded photos, rendered from S3/Instagram URLs that
    // share no common prefix with the app's own assets — the surfaces carry
    // `ph-no-capture` instead.
    blockSelector: '.ph-no-capture',
  },
  loaded: (ph) => {
    if (isSampledForReplay(ph.get_session_id(), REPLAY_SAMPLE_RATE)) {
      ph.startSessionRecording()
    }
  },
  debug: posthogDebugEnabled,
})

declare global {
  interface Window {
    posthog?: typeof posthog
  }
}

// Dev-only: allows `posthog.featureFlags.overrideFeatureFlags(...)` from the browser console.
if (typeof window !== 'undefined' && posthogDebugEnabled) {
  window.posthog = posthog
}

// After a deploy, clients on the old HTML 404 on renamed chunks and the page breaks.
// Reload once (per session) to pick up the new build instead of leaving a dead page.
if (typeof window !== 'undefined') {
  window.addEventListener(
    'error',
    (event) => {
      const src = (event.target as HTMLScriptElement | null)?.src ?? ''
      const isChunkFailure =
        src.includes('/_next/static/') || /ChunkLoadError|Loading chunk/.test(event.message ?? '')
      if (isChunkFailure && !sessionStorage.getItem('chunk_reloaded')) {
        sessionStorage.setItem('chunk_reloaded', '1')
        window.location.reload()
      }
    },
    true,
  )
}
